import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { authService, AuthProfile, LoginResult } from '../../services/authService';
import { StoredSession } from '../../services/authStorage';

interface AuthState {
  token: string | null;
  username: string | null;
  role: string | null;
  mustChangePassword: boolean;
  loading: boolean;
  error: string | null;
}

const initialState: AuthState = {
  token: null,
  username: null,
  role: null,
  mustChangePassword: false,
  loading: false,
  error: null,
};

const errorMessage = (error: unknown, fallback: string) => {
  const response = (error as { response?: { data?: { erreur?: string } } })?.response;
  if (!response) {
    return 'Impossible de contacter le serveur.';
  }
  return response.data?.erreur || fallback;
};

export const login = createAsyncThunk<LoginResult, { username: string; password: string }, { rejectValue: string }>(
  'auth/login',
  async ({ username, password }, { rejectWithValue }) => {
    try {
      return await authService.login(username, password);
    } catch (error) {
      return rejectWithValue(errorMessage(error, 'Identifiants incorrects'));
    }
  }
);

export const changePassword = createAsyncThunk<
  AuthProfile,
  { currentPassword: string; newPassword: string },
  { rejectValue: string }
>(
  'auth/changePassword',
  async (payload, { rejectWithValue }) => {
    try {
      return await authService.changePassword(payload.currentPassword, payload.newPassword);
    } catch (error) {
      return rejectWithValue(errorMessage(error, 'Impossible de changer le mot de passe.'));
    }
  }
);

export const logout = createAsyncThunk('auth/logout', () => authService.logout());

const applyProfile = (state: AuthState, profile: AuthProfile) => {
  state.username = profile.username;
  state.role = profile.role;
  state.mustChangePassword = profile.mustChangePassword;
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setToken: (state, action: PayloadAction<string | null>) => { state.token = action.payload; },
    setSession: (state, action: PayloadAction<StoredSession>) => {
      state.token = action.payload.token;
      state.username = action.payload.username;
      state.role = action.payload.role;
      state.mustChangePassword = action.payload.mustChangePassword;
    },
    setProfile: (state, action: PayloadAction<AuthProfile>) => {
      applyProfile(state, action.payload);
    },
    setMustChangePassword: (state, action: PayloadAction<boolean>) => {
      state.mustChangePassword = action.payload;
    },
    clearAuth: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.token = action.payload.token;
        applyProfile(state, action.payload);
        state.error = null;
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? 'Identifiants incorrects';
      })
      .addCase(changePassword.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(changePassword.fulfilled, (state, action) => {
        state.loading = false;
        applyProfile(state, action.payload);
        state.error = null;
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? 'Impossible de changer le mot de passe.';
      })
      .addCase(logout.fulfilled, () => initialState);
  },
});

export const { setToken, setSession, setProfile, setMustChangePassword, clearAuth } = authSlice.actions;
/** Administrator level: administrator or super administrator. */
export const isAdminRole = (role: string | null | undefined) =>
  role === 'ROLE_ADMIN' || role === 'ROLE_SUPER_ADMIN';
/** Only the super administrator can change the login / password of any account. */
export const isSuperAdminRole = (role: string | null | undefined) => role === 'ROLE_SUPER_ADMIN';
export const isSuperAgentRole = (role: string | null | undefined) => role === 'ROLE_SUPER_AGENT';
export const isAdminOrSuperAgentRole = (role: string | null | undefined) =>
  isAdminRole(role) || isSuperAgentRole(role);
export default authSlice.reducer;
